import { logoutApi } from '@/api/auth.api'
import { getPendingSessionRefresh } from '@/api'
import { getSessionGeneration, resetSessionScope } from '@/api/sessionScope'

/**
 * How long an authentication transition waits for an already-running token
 * refresh to settle before proceeding. Long enough for a normal round trip,
 * short enough that a hung request cannot deadlock the logout.
 */
const PENDING_REFRESH_WAIT_MS = 4_000

/**
 * Waits (bounded) for a token refresh that is already in flight (SIB-924).
 *
 * Ordering is the point: the refresh REQUEST was sent with the departing
 * session's cookies, and the browser applies its response's Set-Cookie
 * whenever it arrives. By letting it settle before the logout/login requests
 * are sent, its response is processed first in the normal completed-request
 * flow, so a completed old refresh can never re-apply cookies AFTER the
 * logout cleared them or the new login set fresh ones.
 *
 * @returns true when there was nothing to wait for or the refresh settled
 * (success or failure both count — settled means its response is done);
 * false when it was still pending after the bounded wait. A timeout does NOT
 * mean the server stopped processing it — the caller must treat that request
 * as uncertain and report it.
 */
export async function settlePendingAuthWork(
  timeoutMs = PENDING_REFRESH_WAIT_MS
): Promise<boolean> {
  const pending = getPendingSessionRefresh()
  if (!pending) return true

  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      pending.then(
        () => true,
        () => true
      ),
      new Promise<false>(resolve => {
        timer = setTimeout(() => resolve(false), timeoutMs)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

/**
 * The single way to leave an authenticated session (SIB-924).
 *
 * Sequence:
 * 1. Capture any already-running token refresh (it was sent with the
 *    departing session's cookies).
 * 2. Reset the client session scope immediately — the departing user's
 *    visible data and caches are gone right away, and no NEW refresh can
 *    start for this transition (stale requests skip the refresh path).
 * 3. Wait (bounded) for the captured refresh to settle, so its response —
 *    including Set-Cookie — is processed before the logout request is sent.
 * 4. Call the server logout. Its cookie clearing now orders after the
 *    refresh's cookies in the normal completed-request flow.
 *
 * Local cleanup is never skipped because the server was unreachable. There
 * is no deadlock risk: the refresh path never waits on logout, and the wait
 * in step 3 is bounded.
 *
 * @returns whether the SERVER-side logout succeeded, and whether a pending
 * refresh settled in time. `pendingRefreshSettled: false` means a refresh
 * request was still unanswered when logout proceeded — the server may still
 * process it, and its late response may re-apply session cookies; callers
 * keep navigating to login, but must not claim the hand-over was fully clean.
 */
export async function endUserSession(): Promise<{
  serverLogoutOk: boolean
  pendingRefreshSettled: boolean
}> {
  const gen = getSessionGeneration()
  const pendingSettled = settlePendingAuthWork()

  // Immediate local teardown; guarded so a logout completing after an even
  // newer session began cannot tear that session down.
  if (gen === getSessionGeneration()) {
    resetSessionScope()
  }

  const pendingRefreshSettled = await pendingSettled
  if (!pendingRefreshSettled) {
    console.warn(
      '[session] A token refresh was still unanswered when logout proceeded; ' +
        'its late response may re-apply session cookies until the next login.'
    )
  }

  let serverLogoutOk = true
  try {
    await logoutApi()
  } catch (e) {
    serverLogoutOk = false
    console.warn('[session] Server logout failed; local session state is cleared anyway:', e)
  }
  return { serverLogoutOk, pendingRefreshSettled }
}

/**
 * Establish a clean session scope for a newly authenticated identity.
 * Called after a successful login BEFORE the new user is exposed to the app,
 * so nothing from a previous session (same tab, no reload) can be served to
 * the new identity — including a different user with the same role.
 *
 * Callers that are about to SEND a login request must first await
 * {@link settlePendingAuthWork} so an old refresh response cannot arrive
 * after the login response (see LoginPage).
 */
export function beginUserSession(): void {
  resetSessionScope()
}
